/** Presentation the preflight field payload does not carry: which section of an item's
 *  field list a field sits in, and a warning a particular value deserves.
 *
 *  `ActionField` has no `group` or `warning` (backend gap, plan 2026-10-08 P5-console), so
 *  these live here, keyed on what a field IS rather than on an action or a restech: the
 *  catalogue questions are every field written under `catalogue.<restech>.*`, whichever
 *  restech has catalogue settings. A field no rule claims renders exactly as before, so an
 *  item without such fields looks the same as it always did.
 */
import type { PreflightActionField } from '@/api/preflight';
import type { InlineEditValue } from '@/components/InlineEdit/InlineEditField';
import type { HelpStoryId } from '@/help/registry.gen';

interface FieldGroupRule {
  label: string;
  /** The help story the section heading links to. */
  story?: HelpStoryId;
  test: (f: PreflightActionField) => boolean;
}

/** The remote check-in question and its companion (the booking system's own switches). */
const CHECKIN_KEYS: ReadonlySet<string> = new Set(['remote_checkin_enabled', 'update_restech_on_checkin']);

/** First match wins; the order here is the order the sections render in. */
const FIELD_GROUP_RULES: readonly FieldGroupRule[] = [
  { label: 'Check-in', test: (f) => CHECKIN_KEYS.has(f.key) },
  {
    label: 'Catalogue automation',
    story: 'catalogue-sync',
    // The gate itself (`<restech>.catalogue_sync`) and the tenant-level answers
    // (`catalogue.<restech>.ai_sync.tours`, `…on_upstream_change`, `…alerts.*`).
    test: (f) => f.key === 'catalogue_sync' || f.path.startsWith('catalogue.'),
  },
];

export interface FieldSection {
  /** null: the item's own, ungrouped fields — rendered first, with no heading. */
  label: string | null;
  story?: HelpStoryId;
  fields: PreflightActionField[];
}

/** Partition `fields` (order kept within each section) into the ungrouped fields and one
 *  section per group rule that claims any. */
export function fieldSections(fields: PreflightActionField[]): FieldSection[] {
  const groupOf = (f: PreflightActionField) => FIELD_GROUP_RULES.find((r) => r.test(f))?.label ?? null;
  const out: FieldSection[] = [{ label: null, fields: fields.filter((f) => groupOf(f) === null) }];
  for (const rule of FIELD_GROUP_RULES) {
    const claimed = fields.filter((f) => groupOf(f) === rule.label);
    if (claimed.length > 0) out.push({ label: rule.label, story: rule.story, fields: claimed });
  }
  return out.filter((s) => s.fields.length > 0);
}

/** The consequence of a value, said on the field while it holds that value. */
const VALUE_WARNINGS: Record<string, (v: InlineEditValue) => string | undefined> = {
  catalogue_ai_tours: (v) =>
    v === false
      ? 'With tours off the sync builds no tours, so bookings import only once ops build or map tours by hand.'
      : undefined,
};

export function fieldWarning(f: PreflightActionField, value: InlineEditValue): string | undefined {
  return VALUE_WARNINGS[f.key]?.(value);
}
